import { Clipboard } from '@angular/cdk/clipboard';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Subject,
  Subscription,
} from 'rxjs';
import { DistrictSimpleDto } from 'src/app/common/dtos/district.dto';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { selectOptionsFromEnumCapitalized } from 'src/app/common/utilities/enum-helpers';
import {
  checkIfE2L,
  checkIfE2LOperator,
} from 'src/app/common/utilities/role-helpers';
import { DistrictSelectComponent } from 'src/app/private/shared/components/district-select/district-select.component';
import { ModalComponent } from 'src/app/private/shared/components/modals/modal/modal.component';
import { ProformLivePreviewModalComponent } from 'src/app/private/shared/components/modals/proform-live-preview-modal/proform-live-preview-modal.component';
import { ProformsReorderSectionsModalComponent } from 'src/app/private/shared/components/proforms/proforms-admin/proforms-reorder-sections-modal/proforms-reorder-sections-modal.component';
import { AlertService } from 'src/app/private/shared/services/alert/alert.service';
import { ProformsAdminService } from 'src/app/private/shared/services/proforms/proforms-admin.service';
import { ProformsSortingService } from 'src/app/private/shared/services/proforms/proforms-sorting.service';
import { FEATURE_FLAGS } from 'src/app/private/shared/services/unleash/unleash.helpers';
import { UnleashService } from 'src/app/private/shared/services/unleash/unleash.service';
import {
  CreateProFormItemPayload,
  UpdateProFormPayload,
} from 'src/app/private/shared/types/payloads/proforms.payloads';
import {
  ProFormAnonymity,
  ProFormsAPIResponse,
  ProFormsQuestionAPIResponse,
  ProFormsSectionAPIResponse,
  ProFormStatus,
  ProFormType,
} from 'src/app/private/shared/types/responses/proforms.responses';

export interface ProformTogglePayload {
  anonymity: ProFormAnonymity;
  is_shared?: number;
}

@Component({
  selector: 'app-proforms-form-builder-page',
  standalone: false,
  templateUrl: './proforms-form-builder-page.component.html',
  styleUrl: './proforms-form-builder-page.component.scss',
})
export class ProformsFormBuilderPageComponent implements OnInit, OnDestroy {
  @ViewChild('titleInput') titleInput: ElementRef;

  @ViewChild('shareFormAcrossDistricts') shareFormAcrossDistricts: ElementRef;

  @ViewChild('shareWithDistrictSelect')
  shareWithDistrictSelect: DistrictSelectComponent;

  @ViewChild('excludeFromDistrictSelect')
  excludeFromDistrictSelect: DistrictSelectComponent;

  @ViewChild('header') header!: ElementRef;

  @ViewChild('outroBody') outroBody!: ElementRef;

  @ViewChild('previewModal') previewModal!: ProformLivePreviewModalComponent;

  @ViewChild('reorderSectionsModal')
  reorderSectionsModal: ProformsReorderSectionsModalComponent;

  @ViewChild('quizTypeConfirmModal') quizTypeConfirmModal: ModalComponent;

  subs: Subscription[] = [];

  user: User;

  formId: number;

  form: ProFormsAPIResponse;

  liveSectionTitles: Record<number, string> = {};

  activeSectionId: number | null = null;

  private autoActivateDone = false;

  isSidebarOpen = true;

  isEditingOutro = false;

  isArchived = false;

  proFormType = ProFormType;

  types: { key: string; value: string }[];

  statuses: { key: string; value: string }[];

  districtsSharedWith: DistrictSimpleDto[];

  districtsExcludedFrom: DistrictSimpleDto[];

  isStuck = false;

  headerOffset = 0;

  title = '';

  titleSubject: Subject<string> = new Subject<string>();

  passingGradeControl: FormControl;

  proformAnonymousEnabled = false;

  proformQuizzesEnabled = false;

  copyTooltipMessage = 'Copy URL';

  sharingUrl: string;

  copyIcon = 'copy';

  ProFormAnonymity = ProFormAnonymity;

  isE2L = false;

  private previousFormType: ProFormType | null = null;

  private quizConfirmPending = false;

  isE2LOperator = false;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.isStuck = window.scrollY > this.headerOffset;
  }

  // Close the editor when clicking outside the card-body
  // Listen for clicks anywhere in the document
  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    // Check if the user is currently editing and if the click is outside the outro body
    if (
      this.isEditingOutro &&
      this.outroBody &&
      !this.outroBody.nativeElement.contains(event.target)
    ) {
      this.isEditingOutro = false;
    }
  }

  @HostListener('window:resize', [])
  hideSidebarOnMobile() {
    if (window.innerWidth <= 1200) {
      this.isSidebarOpen = window.innerWidth > 1200;
    }
  }

  onTitleChange(newTitle: string) {
    this.titleSubject.next(newTitle); // Emit the new title value to trigger debounce
  }

  // eslint-disable-next-line class-methods-use-this
  backToSettings() {
    window.history.back();
  }

  // Custom validator to disallow decimals
  // eslint-disable-next-line class-methods-use-this
  wholeNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value == null || value === '') return null; // Let required validator handle emptiness
    return Number.isInteger(+value) ? null : { notWholeNumber: true };
  }

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    public router: Router,
    private proformsAdminService: ProformsAdminService,
    private alertService: AlertService,
    private featureFlagService: UnleashService,
    private clipboard: Clipboard,
    private proformsSortingService: ProformsSortingService
  ) {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
    this.isE2L = checkIfE2L(this.user);
    this.proformQuizzesEnabled = this.featureFlagService.isEnabled(
      FEATURE_FLAGS.proformQuizzes
    );
    this.proformAnonymousEnabled = this.featureFlagService.isEnabled(
      FEATURE_FLAGS.proformAnonymous
    );
  }

  ngOnInit() {
    // APPLY BACKGROUND IMAGE
    document.body.classList.add('proformbuilder');

    this.route.params.subscribe((params) => {
      this.formId = parseInt(params['formId']);
      this.proformsAdminService.getProForm(this.formId);
    });

    this.isE2LOperator = checkIfE2LOperator(this.user as User);

    this.subs.push(
      this.proformsAdminService.currentProForm.subscribe((response) => {
        if (response) {
          this.previousFormType = response.type;
          this.form = response;
          this.sharingUrl =
            this.form.anonymity === ProFormAnonymity.NONE
              ? `${window.location.origin}/proforms/form/${this.form.id}`
              : `${window.location.origin}/public/pro-form/${this.form.id}`;
          this.isArchived = this.form.status === ProFormStatus.ARCHIVED;
          if (this.form.districtsSharedWith) {
            this.districtsSharedWith = this.form.districtsSharedWith.map(
              (district) => district.district
            );
          }
          if (this.form.districtsExcludedFrom) {
            this.districtsExcludedFrom = this.form.districtsExcludedFrom.map(
              (district) => district.district
            );
          }
          // Sort questions by sort_order within each section
          if (this.form.sections) {
            this.form.sections.forEach((section) => {
              if (section.questions) {
                section.questions.sort((a, b) => a.sort_order - b.sort_order);
              }
            });
          }
          // Define passing grade form control for instant validation & value changes
          this.passingGradeControl = new FormControl(this.form.passing_grade, [
            Validators.required,
            Validators.min(0),
            Validators.max(100),
            this.wholeNumberValidator,
          ]);

          this.passingGradeControl.valueChanges
            .pipe(
              debounceTime(500),
              distinctUntilChanged(),
              filter(() => this.passingGradeControl.valid)
            )
            .subscribe(() => {
              this.updatePassingGrade();
            });

          if (!this.autoActivateDone) {
            this.autoActivateDone = true;
            setTimeout(() => {
              if (this.form.sections) {
                if (this.form.sections[0].questions.length === 1) {
                  this.proformsAdminService.setActiveQuestion(
                    this.form.sections[0].questions[0].id
                  );
                }
              }
            }, 500);
          }
          setTimeout(() => {
            this.headerOffset =
              this.header.nativeElement.getBoundingClientRect().top +
              window.scrollY;
          }, 0);
        }
      })
    );

    this.subs.push(
      this.proformsAdminService.currentActiveSection.subscribe((id) => {
        this.activeSectionId = id;
      })
    );

    this.hideSidebarOnMobile();

    this.titleSubject
      .pipe(
        debounceTime(500), // Wait for 500ms after the user stops typing
        distinctUntilChanged() // Only emit if the value has changed
      )
      .subscribe((value: string) => {
        if (value !== this.title) {
          this.updateTitle(value);
        }
      });

    this.statuses = selectOptionsFromEnumCapitalized(ProFormStatus).filter(
      (status) => status.key !== 'deleted'
    );

    this.types = selectOptionsFromEnumCapitalized(ProFormType);
  }

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  deleteForm() {
    this.proformsAdminService.deleteForm(this.formId).subscribe({
      next: () => {
        this.alertService.showAlert('Form Deleted');
        window.history.back();
      },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  trackByQuestionId(
    _index: number,
    question: ProFormsQuestionAPIResponse
  ): number {
    return question.id;
  }

  // eslint-disable-next-line class-methods-use-this
  trackBySectionId(
    _index: number,
    section: ProFormsSectionAPIResponse
  ): number {
    return section.id;
  }

  openReorderSectionsModal() {
    if (this.form?.sections) {
      this.reorderSectionsModal.openModal(this.form.sections);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  dropQuestion(
    phase: ProFormsQuestionAPIResponse[],
    event: CdkDragDrop<ProFormsQuestionAPIResponse[]>
  ) {
    if (event.previousContainer === event.container) {
      // Same-section reorder — existing behavior
      const sortData = this.proformsSortingService.calcSortChange(
        phase,
        event.previousIndex,
        event.currentIndex
      );
      moveItemInArray(phase, event.previousIndex, event.currentIndex);
      if (sortData) {
        this.subs.push(
          this.proformsSortingService
            .sortQuestion(sortData.id, sortData.direction, sortData.refId)
            .subscribe()
        );
      }
    } else {
      // Cross-section move
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );

      const destSection = this.form.sections?.find(
        (s) => s.questions === event.container.data
      );
      if (!destSection) return;

      const movedQuestion = event.container.data[event.currentIndex];
      movedQuestion.proform_form_section_id = destSection.id;

      this.proformsAdminService
        .moveQuestionToSection(movedQuestion.id, destSection.id)
        .subscribe({
          next: () => {
            const dest = event.container.data;
            if (dest.length > 1) {
              const isFirst = event.currentIndex === 0;
              const refIndex = isFirst ? 1 : event.currentIndex - 1;
              const direction = isFirst ? 'before' : 'after';
              this.proformsSortingService
                .sortQuestion(movedQuestion.id, direction, dest[refIndex].id)
                .subscribe();
            }
          },
        });
    }
  }

  updateTitle(newTitle: string) {
    this.title = newTitle; // Update local title value
    this.proformsAdminService.updateProForm(this.formId, { title: newTitle });
  }

  updateStatus() {
    this.proformsAdminService.updateProForm(this.formId, {
      status: this.form.status,
    });
    this.isArchived = this.form.status === ProFormStatus.ARCHIVED;
  }

  updateType() {
    // Guard: ignore ngModelChange calls that fire as a side-effect of our own
    // programmatic revert (ng-select fires onChange even on writeValue).
    if (this.quizConfirmPending) return;

    if (this.form.type === ProFormType.QUIZ) {
      this.quizConfirmPending = true;
      // Revert the ngModel change so the dropdown shows the previous type
      // while the confirmation modal is open.
      this.form.type = this.previousFormType ?? ProFormType.SURVEY;
      this.quizTypeConfirmModal.open();
      return;
    }
    this.applyTypeUpdate();
  }

  confirmQuizTypeChange() {
    this.quizConfirmPending = false;
    this.quizTypeConfirmModal.close();
    this.form.type = ProFormType.QUIZ;
    this.applyTypeUpdate();
  }

  cancelQuizTypeChange() {
    this.quizConfirmPending = false;
    this.quizTypeConfirmModal.close();
  }

  private applyTypeUpdate() {
    const payload: UpdateProFormPayload = { type: this.form.type };

    if (
      this.form.type !== ProFormType.SURVEY &&
      this.form.anonymity === ProFormAnonymity.REQUIRED
    ) {
      this.form.anonymity = ProFormAnonymity.NONE;
      payload.anonymity = ProFormAnonymity.NONE;
      this.sharingUrl = `${window.location.origin}/proforms/form/${this.form.id}`;
    }

    // The backend clears conditional section settings when switching to quiz.
    // updateProForm already refreshes the form state from the API response.
    this.proformsAdminService.updateProForm(this.formId, payload);
  }

  updatePassingGrade() {
    this.proformsAdminService.updateProForm(this.formId, {
      passing_grade: this.passingGradeControl.value,
    });
  }

  updateShowMissed($event: Event) {
    const inputElement = $event.target as HTMLInputElement;
    const showMissed = inputElement.checked ? 1 : 0;

    this.form.show_missed = showMissed;

    const updatePayload: UpdateProFormPayload = {
      show_missed: showMissed,
    };

    // If "Show missed" is turned OFF, also turn off "Show answers"
    if (!showMissed && this.form.show_answers) {
      this.form.show_answers = 0;
      updatePayload.show_answers = 0;
    }

    this.proformsAdminService.updateProForm(this.formId, updatePayload);
  }

  updateShowAnswers($event: Event) {
    const inputElement = $event.target as HTMLInputElement;
    this.form.show_answers = inputElement.checked ? 1 : 0;
    this.proformsAdminService.updateProForm(this.formId, {
      show_answers: this.form.show_answers,
    });
  }

  toggleLock($event: Event) {
    const inputElement = $event.target as HTMLInputElement;
    this.form.is_locked = inputElement.checked ? 1 : 0;
    this.proformsAdminService.setProFormLocked(
      this.formId,
      this.form.is_locked
    );
  }

  toggleSharing($event: Event) {
    const inputElement = $event.target as HTMLInputElement;
    this.form.is_shared = inputElement.checked ? 1 : 0;
    this.proformsAdminService.updateProForm(this.formId, {
      is_shared: this.form.is_shared,
    });
  }

  toggleAnonymity($event: Event) {
    const inputElement = $event.target as HTMLInputElement;
    this.form.anonymity = inputElement.checked
      ? ProFormAnonymity.REQUIRED
      : ProFormAnonymity.NONE;

    const updatePayload: ProformTogglePayload = {
      anonymity: this.form.anonymity,
    };

    if (inputElement.checked && this.form.is_shared) {
      this.shareFormAcrossDistricts.nativeElement.checked = false;
      updatePayload.is_shared = 0;
    }

    this.proformsAdminService.updateProForm(this.formId, updatePayload);
  }

  toggleInstructionalInsightsExport($event: Event) {
    const inputElement = $event.target as HTMLInputElement;
    this.form.instructional_insights_export = inputElement.checked ? 1 : 0;
    const updatePayload: UpdateProFormPayload = {
      instructional_insights_export: this.form.instructional_insights_export,
    };
    this.proformsAdminService.updateProForm(this.formId, updatePayload);
  }

  updateOutro(outro: string) {
    this.proformsAdminService.updateProForm(this.formId, { outro });
  }

  showEditOutro(event: MouseEvent) {
    event.stopPropagation();
    this.isEditingOutro = true;
  }

  handleShareDistricts(incomingDistricts: DistrictSimpleDto[]) {
    this.subs.push(
      this.proformsAdminService
        .handleShareDistricts(incomingDistricts)
        .subscribe((response: number[]) => {
          const incomingDistrictIds = response;
          // Update the exclude dropdown's internalSelectedDistrictList
          this.excludeFromDistrictSelect.internalSelectedDistrictList =
            this.excludeFromDistrictSelect.internalSelectedDistrictList.filter(
              (district) => !incomingDistrictIds.includes(district.id)
            );
          this.alertService.showAlert('Sharing Settings Saved');
        })
    );
  }

  handleExcludeDistricts(incomingDistricts: DistrictSimpleDto[]) {
    this.subs.push(
      this.proformsAdminService
        .handleExcludeDistricts(incomingDistricts)
        .subscribe((response: number[]) => {
          const incomingDistrictIds = response;
          // Update the share dropdown's internalSelectedDistrictList
          this.shareWithDistrictSelect.internalSelectedDistrictList =
            this.shareWithDistrictSelect.internalSelectedDistrictList.filter(
              (district) => !incomingDistrictIds.includes(district.id)
            );
          this.alertService.showAlert('Sharing Settings Saved');
        })
    );
  }

  createQuestion(sectionId: number) {
    const payload: CreateProFormItemPayload = {
      field_type: 'radio',
      question: 'Question',
      helper_text: '',
    };
    this.proformsAdminService.createQuestion(sectionId, payload);
  }

  copyUrl(tooltip: NgbTooltip) {
    tooltip.close();
    const url = this.sharingUrl;
    this.clipboard.copy(url);
    this.copyTooltipMessage = 'Link copied to clipboard!';
    setTimeout(() => {
      tooltip.open();
      this.copyIcon = 'check-lg';
    }, 250);
    setTimeout(() => {
      if (tooltip.isOpen()) {
        tooltip.close();
      }
      this.copyIcon = 'copy';
      this.copyTooltipMessage = 'Copy URL';
    }, 2500);
  }

  ngOnDestroy() {
    // REMOVE BACKGROUND IMAGE
    document.body.classList.remove('proformbuilder');
    let sub = this.subs.pop();
    while (sub) {
      sub.unsubscribe();
      sub = this.subs.pop();
    }
  }
}
